import { useState, useEffect } from 'react';
import { Button, Checkbox, Divider, Input } from 'antd';
import Tooltip from './Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import { scrollToBoxById } from '../Middle/InfinitePage';
import { EditorState, SelectionState } from 'draft-js';
import { useOverlay } from '../../contexts/OverlayContext';

export const SearchReplacePopover = ({ onClose }) => {
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  const {
    setSearchParams,
    computeSearchResults,
    nextSearchResult,
    prevSearchResult,
    searchResults,
    searchIndex
  } = useStore((state) => ({
    setSearchParams: state.setSearchParams,
    computeSearchResults: state.computeSearchResults,
    nextSearchResult: state.nextSearchResult,
    prevSearchResult: state.prevSearchResult,
    searchResults: state.searchResults,
    searchIndex: state.searchIndex,
  }));
  const clearSearchResults = useStore((state) => state.clearSearchResults);
  const setSearchScope = useStore((state) => state.setSearchScope);
  const setSearchUiFocus = useStore((state) => state.setSearchUiFocus);
  const setSearchSelectionActive = useStore((state) => state.setSearchSelectionActive);
  const setSearchIndex = useStore((state) => state.setSearchIndex);

  const boxes = useStore((state) => state.boxes);
  const masks = useStore((state) => state.masks);
  const setSelectedBoxId = useStore((state) => (id) => state.handleSelectBox({ box: state.getBox(id) }));
  const updateBox = useStore((state) => state.updateBox);
  const { overlayProps } = useOverlay();
  const currentScope = overlayProps?.left?.shownStatus === 'variables' ? 'variables' : 'document';
  const searchButtonLabel = currentScope === 'variables' ? 'Rechercher dans le formulaire' : 'Rechercher dans le document';
  const handleBoxVariableSelectedId = useStore((state) => state.handleBoxVariableSelectedId);
  const requestScrollToBox = useStore((state) => state.requestScrollToBox);
  const currentDocument = useStore((state) => state.currentDocument);
  const searchQuery = useStore((state) => state.searchQuery);

  // Réinitialiser le texte de recherche lorsque le document change ou que searchQuery est vidé
  useEffect(() => {
    if (!searchQuery) {
      setSearchText('');
      setReplaceText('');
      setIsReplaceMode(false);
    }
  }, [currentDocument?.id, currentDocument?.type, searchQuery]);

  // Ne lance plus automatiquement la recherche
  // useEffect(() => {
  //   setSearchParams({ query: searchText, matchCase, wholeWord });
  //   computeSearchResults();
  // }, [searchText, matchCase, wholeWord, setSearchParams, computeSearchResults]);

  const performSearch = () => {
    // Fixer le scope en fonction de l'overlay (document / variables)
    setSearchScope(currentScope);
    // Forcer un reset des résultats si le scope a changé via overlay
    // (la détection se fait dans computeSearchResults)
    setSearchParams({ query: searchText, matchCase, wholeWord });
    computeSearchResults();

    // Si des résultats sont trouvés, aller à la première occurrence
    setTimeout(() => {
      const { searchResults } = useStore.getState();
      if (searchResults.length > 0) {
        const firstResult = searchResults[0];
        if (currentScope === 'variables') {
          // Activer la sélection côté variable et laisser FormViewer scroller
          handleBoxVariableSelectedId(firstResult.boxId);
          requestScrollToBox(firstResult.boxId);
          setSearchSelectionActive(true);
        } else {
          const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
          // Pour les tableaux, on doit scroller vers le tableau et sélectionner la cellule/header
          if (firstResult.isTable) {
            scrollToBoxById(firstResult.boxId, allBoxes);
            setSelectedBoxId(firstResult.boxId);
            setTimeout(() => {
              selectTextInEditor(firstResult);
              setTimeout(() => {
                const state = useStore.getState();
                const active = state.searchResults?.[state.searchIndex];
                if (active && active.isTable) {
                  let wrapperId;
                  if (active.isHeader) {
                    wrapperId = `${active.boxId}-${active.columnKey}`.split('.').join("") + '-editor-wrapper';
                  } else {
                    wrapperId = `${active.boxId}-${active.columnKey}-${active.rowKey}`.split('.').join("") + '-editor-wrapper';
                  }
                  const wrapper = document.getElementById(wrapperId);
                  if (wrapper) {
                    const activeSpan = wrapper.querySelector(
                      `span.search-highlight[data-block-key="${active.blockKey}"][data-start="${active.start}"][data-end="${active.end}"]`
                    );
                    if (activeSpan) {
                      // Vérifier si l'élément est déjà visible avant de scroller
                      // On considère qu'un élément est visible s'il est au moins partiellement dans la viewport
                      const rect = activeSpan.getBoundingClientRect();
                      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                      const isVisible = (
                        rect.top < viewportHeight &&
                        rect.bottom > 0 &&
                        rect.left < viewportWidth &&
                        rect.right > 0
                      );
                      // Ne scroller que si l'élément n'est pas déjà visible (même partiellement)
                      if (!isVisible) {
                        activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }
                  }
                }
              }, 200);
            }, 100);
          } else {
            scrollToBoxById(firstResult.boxId, allBoxes);
            setSelectedBoxId(firstResult.boxId);
            selectTextInEditor(firstResult);
          }
        }
      }
    }, 100);
  };

  const selectTextInEditor = (result) => {
    const { boxId, blockKey, start, end, isTable, rowKey, columnKey } = result;
    const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
    const box = allBoxes.find(b => b.id === boxId);
    if (!box) return;

    // Gérer les tableaux
    if (isTable && columnKey) {
      // Sélectionner la cellule ou l'en-tête du tableau
      if (result.isHeader) {
        // Sélectionner l'en-tête (header)
        updateBox(boxId, (b) => {
          b.selectedItem = { columnKey, type: 'columns' };
        });
      } else if (rowKey) {
        // Sélectionner la cellule de données
        updateBox(boxId, (b) => {
          b.selectedItem = { rowKey, columnKey, type: 'data' };
        });
      }
      // Note: La sélection du texte dans la cellule/header sera gérée par le composant Cell/Header
      // qui détecte le changement de selectedItem et met le focus
      return;
    }

    // Gérer les boxes de texte normales
    if (!box.content?.editorState) return;

    const editorState = box.content.editorState;
    const selection = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: start,
      focusOffset: end,
    });
    const newEditorState = EditorState.forceSelection(editorState, selection);

    updateBox(boxId, (box) => {
      box.content.editorState = newEditorState;
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      performSearch();
      // Perdre le focus de l'input
      e.currentTarget.blur();
      setSearchUiFocus(false);
    }
  };

  return (
    <div style={{ width: 320, padding: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          allowClear
          placeholder={isReplaceMode ? 'Rechercher…' : 'Rechercher…'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setSearchUiFocus(true)}
          onBlur={() => setSearchUiFocus(false)}
        />
        <Tooltip title={isReplaceMode ? 'Passer en mode recherche' : 'Passer en mode remplacement'} placement='bottom'>
          <Button onClick={() => {
            const next = !isReplaceMode;
            setIsReplaceMode(next);
            if (next) {
              // Activer le verrou d'édition côté formulaire lorsque l'on entre en mode remplacement
              setSearchSelectionActive(true);
            }
          }} onMouseDown={(e) => e.preventDefault()}>
            <FontAwesomeIcon icon={icon({ name: 'arrow-right-arrow-left', style: 'light' })} />
          </Button>
        </Tooltip>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <Checkbox checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)}>
          Respecter la casse
        </Checkbox>
        <Checkbox checked={wholeWord} onChange={(e) => setWholeWord(e.target.checked)}>
          Mot entier
        </Checkbox>
      </div>

      {isReplaceMode && (
        <div style={{ marginTop: 8 }}>
          <Input
            allowClear
            placeholder={'Remplacer par…'}
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
            onFocus={() => {
              setSearchUiFocus(true);
              // Tant qu'on saisit le texte de remplacement, bloquer la saisie dans le formulaire si un résultat est actif
              if (currentScope === 'variables' && searchResults.length > 0) {
                setSearchSelectionActive(true);
              }
            }}
            onBlur={() => {
              setSearchUiFocus(false);
              // Ne pas forcément désactiver le verrou ici; il sera retiré à la fermeture du tooltip
            }}
          />
        </div>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Bouton Rechercher */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <Button
          disabled={!searchText}
          onClick={performSearch}
          style={{ width: '100%' }}
        >
          {searchButtonLabel}
        </Button>
      </div>

      {/* Résultats de recherche */}
      <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 8, minHeight: 18 }}>
        {searchText ? (
          searchResults.length > 0
            ? `${searchResults.length} occurrence(s) trouvée(s)` + (currentScope === 'document' ? ' (hors variables)' : '')
            : 'Aucune occurrence trouvée'
        ) : ''}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={() => { clearSearchResults(); setSearchSelectionActive(false); onClose(); }} onMouseDown={(e) => e.preventDefault()}>Fermer</Button>
        <Button disabled={!searchText || searchResults.length === 0} onClick={() => {
          const res = prevSearchResult();
          if (res) {
            if (currentScope === 'variables') {
              handleBoxVariableSelectedId(res.boxId);
              requestScrollToBox(res.boxId);
              setSearchSelectionActive(true);
            } else {
              const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
              // Pour les tableaux, on doit scroller vers le tableau et sélectionner la cellule/header
              if (res.isTable) {
                scrollToBoxById(res.boxId, allBoxes);
                setSelectedBoxId(res.boxId);
                // Sélectionner la cellule ou l'en-tête
                setTimeout(() => {
                  selectTextInEditor(res);
                  // Attendre un peu pour que le DOM soit mis à jour, puis scroller vers le résultat
                  setTimeout(() => {
                    const state = useStore.getState();
                    const active = state.searchResults?.[state.searchIndex];
                    if (active && active.isTable) {
                      // Construire l'ID du wrapper selon le type
                      let wrapperId;
                      if (active.isHeader) {
                        wrapperId = `${active.boxId}-${active.columnKey}`.split('.').join("") + '-editor-wrapper';
                      } else {
                        wrapperId = `${active.boxId}-${active.columnKey}-${active.rowKey}`.split('.').join("") + '-editor-wrapper';
                      }
                      const wrapper = document.getElementById(wrapperId);
                      if (wrapper) {
                        const activeSpan = wrapper.querySelector(
                          `span.search-highlight[data-block-key="${active.blockKey}"][data-start="${active.start}"][data-end="${active.end}"]`
                        );
                        if (activeSpan) {
                          // Vérifier si l'élément est déjà visible avant de scroller
                          const rect = activeSpan.getBoundingClientRect();
                          const isVisible = (
                            rect.top >= 0 &&
                            rect.left >= 0 &&
                            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                          );
                          // Ne scroller que si l'élément n'est pas déjà visible
                          if (!isVisible) {
                            activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }
                      }
                    }
                  }, 200);
                }, 100);
              } else {
                scrollToBoxById(res.boxId, allBoxes);
                setSelectedBoxId(res.boxId);
                // Sélectionner le texte trouvé dans l'éditeur
                setTimeout(() => {
                  selectTextInEditor(res);
                }, 100);
              }
            }
          }
        }}>Précédent</Button>
        <Button disabled={!searchText || searchResults.length === 0} onClick={() => {
          const res = nextSearchResult();
          if (res) {
            if (currentScope === 'variables') {
              handleBoxVariableSelectedId(res.boxId);
              requestScrollToBox(res.boxId);
              setSearchSelectionActive(true);
            } else {
              const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
              // Pour les tableaux, on doit scroller vers le tableau et sélectionner la cellule/header
              if (res.isTable) {
                scrollToBoxById(res.boxId, allBoxes);
                setSelectedBoxId(res.boxId);
                // Sélectionner la cellule ou l'en-tête
                setTimeout(() => {
                  selectTextInEditor(res);
                  // Attendre un peu pour que le DOM soit mis à jour, puis scroller vers le résultat
                  setTimeout(() => {
                    const state = useStore.getState();
                    const active = state.searchResults?.[state.searchIndex];
                    if (active && active.isTable) {
                      // Construire l'ID du wrapper selon le type
                      let wrapperId;
                      if (active.isHeader) {
                        wrapperId = `${active.boxId}-${active.columnKey}`.split('.').join("") + '-editor-wrapper';
                      } else {
                        wrapperId = `${active.boxId}-${active.columnKey}-${active.rowKey}`.split('.').join("") + '-editor-wrapper';
                      }
                      const wrapper = document.getElementById(wrapperId);
                      if (wrapper) {
                        const activeSpan = wrapper.querySelector(
                          `span.search-highlight[data-block-key="${active.blockKey}"][data-start="${active.start}"][data-end="${active.end}"]`
                        );
                        if (activeSpan) {
                          // Vérifier si l'élément est déjà visible avant de scroller
                          const rect = activeSpan.getBoundingClientRect();
                          const isVisible = (
                            rect.top >= 0 &&
                            rect.left >= 0 &&
                            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                          );
                          // Ne scroller que si l'élément n'est pas déjà visible
                          if (!isVisible) {
                            activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }
                      }
                    }
                  }, 200);
                }, 100);
              } else {
                scrollToBoxById(res.boxId, allBoxes);
                setSelectedBoxId(res.boxId);
                // Sélectionner le texte trouvé dans l'éditeur
                setTimeout(() => {
                  selectTextInEditor(res);
                }, 100);
              }
            }
          }
        }}>Suivant</Button>

      </div>

      {/* Numéro de l'occurrence courante */}
      {searchResults.length > 0 && (
        <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 8 }}>
          Résultat {searchIndex + 1}/{searchResults.length}
        </div>
      )}

      {isReplaceMode && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button
            disabled={!searchText || searchResults.length === 0}
            onClick={() => {
              const replacement = replaceText || '';
              const prevIdx = searchIndex;
              useStore.getState().replaceActiveOccurrence(replacement);
              // Après recompute, rester sur le même index (l'ex-occurence suivante)
              setTimeout(() => {
                const { searchResults } = useStore.getState();
                if (searchResults.length === 0) return;
                const targetIdx = Math.min(prevIdx, searchResults.length - 1);
                setSearchIndex(targetIdx);
                const res = searchResults[targetIdx];
                if (!res) return;
                if (currentScope === 'variables') {
                  handleBoxVariableSelectedId(res.boxId);
                } else {
                  const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
                  scrollToBoxById(res.boxId, allBoxes);
                  setSelectedBoxId(res.boxId);
                  setTimeout(() => selectTextInEditor(res), 50);
                }
              }, 50);
            }}
          >
            Remplacer
          </Button>
          <Button
            disabled={!searchText || searchResults.length === 0}
            onClick={() => {
              const replacement = replaceText || '';
              useStore.getState().replaceAllOccurrences(replacement);
            }}
          >
            Remplacer tout
          </Button>
        </div>
      )}
    </div>
  );
}

export default SearchReplacePopover;
